import React, { useState, useRef, memo } from 'react';
import { Box, Typography } from '@mui/material';
import OfferCircle from './OfferCircle';
import { Swiper, SwiperSlide } from 'swiper/react';
import { EffectCoverflow } from 'swiper/modules';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/effect-coverflow';

/**
 * OffersScroller Component
 * Displays a horizontal scrolling section of offers with center-focused items
 * using Swiper carousel
 *
 * @param {Object} props
 * @param {Array} props.offers - All available offers
 * @param {Array} props.ownedOffers - Offers the member already owns
 * @param {number} props.memberPoints - Current member points
 * @param {Function} props.onCenterChange - Callback when center offer changes
 */
const OffersScroller = memo(({ offers, memberPoints = 0, onCenterChange, onOfferClick }) => {
  const [currentCenterIndex, setCurrentCenterIndex] = useState(0);
  // Reference to store the Swiper instance
  const swiperRef = useRef(null);

  // Call onCenterChange with the initial centered offer
  React.useEffect(() => {
    if (onCenterChange && offers.length > 0) {
      onCenterChange(offers[0]);
    }
  }, [offers]);

  React.useEffect(() => {
    if (onCenterChange && offers[currentCenterIndex]) {
      onCenterChange(offers[currentCenterIndex]);
    }
  }, [currentCenterIndex]);

  // Handle slide change to update the current center index
  const handleSlideChange = (swiper) => {
    if (swiper.realIndex === currentCenterIndex) return; // Prevent unnecessary updates
    setCurrentCenterIndex(swiper.realIndex);
  };

  // Handle click on a slide
  const handleSlideClick = (index, offer) => {
    // First, slide to the clicked slide to center it
    if (swiperRef.current && swiperRef.current.swiper) {
      swiperRef.current.swiper.slideToLoop(index);
    }

    // Then, if there's an onOfferClick handler, call it
    if (onOfferClick) {
      // Small delay to allow the slide animation to start
      setTimeout(() => {
        onOfferClick(offer);
      }, 100);
    }
  };

  // Custom styles for the carousel
  const customStyles = `
    .swiper {
      width: 100%;
      overflow: visible;
    }

    .swiper-wrapper {
      align-items: center;
    }

    .swiper-slide {
      transition: transform 0.3s ease;
      display: flex;
      justify-content: center;
      cursor: pointer;
    }

    .swiper-slide-active {
      z-index: 10;
    }
  `;

  return (
    <Box sx={{ height: '408px', width: '1200px', display: 'block', overflow: 'hidden', px: 2 }}>
      <style>{customStyles}</style>
      {offers.length > 0 ? (
        <Swiper
          ref={swiperRef}
          modules={[EffectCoverflow]}
          spaceBetween={10}
          slidesPerView={1.8}
          centeredSlides={true}
          loop={offers.length > 3}
          effect="coverflow"
          coverflowEffect={{
            rotate: 0,
            stretch: 0,
            depth: 100,
            modifier: 1.5,
            slideShadows: false,
          }}
          breakpoints={{
            // when window width is >= 600px
            600: {
              slidesPerView: 3.2,
              spaceBetween: 20
            },
            // when window width is >= 800px
            800: {
              slidesPerView: 3.2,
              spaceBetween: 20
            },
            // when window width is >= 1200px
            1200: {
              slidesPerView: 3.2,
              spaceBetween: 20
            },
            // when window width is >= 1600px
            1600: {
              slidesPerView: 3.2,
              spaceBetween: 20
            }
          }}
          sx={{
            width: '500px'
          }}
          onSlideChange={handleSlideChange}
          grabCursor={true}
        >
          {offers.map((offer, index) => (
            <SwiperSlide key={offer.customer_coupon_id || offer.id} onClick={() => handleSlideClick(index, offer)}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  padding: { xs: 1, sm: 1.5, md: 2 },
                  transition: 'all 0.3s ease',
                  transform: {
                    xs: index === currentCenterIndex ? 'scale(1.01)' : 'scale(1)',
                    sm: index === currentCenterIndex ? 'scale(1.05)' : 'scale(1)',
                    md: index === currentCenterIndex ? 'scale(1.1)' : 'scale(1)'
                  },
                  zIndex: index === currentCenterIndex ? 10 : 1,
                  opacity: Math.abs(index - currentCenterIndex) > 1 ? 0.7 : 1,
                  filter: {
                    xs: 'none', // No blur on mobile for better performance
                    sm: index === currentCenterIndex ? 'none' : 'blur(0.1px)',
                    md: index === currentCenterIndex ? 'none' : 'blur(0.2px)'
                  },
                  '&:hover': {
                    cursor: 'pointer'
                  }
                }}
              >
                <OfferCircle
                  offer={offer}
                  memberPoints={memberPoints}
                  isOwned={!!offer.customer_coupon_id && (offer.point_value || 0) === 0}
                  isSelected={index === currentCenterIndex}
                />
              </Box>
            </SwiperSlide>
          ))}
        </Swiper>
      ) : (
        <Box py={4} textAlign="center">
          <Typography color="text.secondary">
            No offers available
          </Typography>
        </Box>
      )}
    </Box>
  );
});

export default OffersScroller;